import {
  ParticipantState,
  SelfPaced,
  V1AccessType,
  V1Challenge,
  V1DurationUnit,
  V1TimeDuration,
} from '@wix/ambassador-challenge-service-web/types';
import {
  IUserPaidPlan,
  getChallengePaidPlans,
  isChallengeFree,
} from '@wix/challenges-web-library/dist/src';
import { IUserType } from '../contexts/User/UserContext';
import { userTypeHandlers } from '../contexts/User/helpers/userTypeHandlers';
import endOfToday from 'date-fns/endOfToday';
import addDays from 'date-fns/addDays';
import endOfDay from 'date-fns/endOfDay';
import isAfter from 'date-fns/isAfter';
import { getRightDateFromBackend } from './dates';

export function isPrivateChallenge(challenge: V1Challenge) {
  return (
    challenge?.settings?.accessRestrictions?.accessType === V1AccessType.PRIVATE
  );
}

export const isOngoing = (challenge: V1Challenge): boolean =>
  Boolean(challenge?.settings?.timelineType?.ongoing);
export const isFlexible = (challenge: V1Challenge): boolean =>
  Boolean(challenge?.settings?.timelineType?.flexible?.duration);
export const isSpecific = (challenge: V1Challenge): boolean =>
  Boolean(challenge?.settings?.timelineType?.specific?.duration);
export const isSelfPaced = (challenge: V1Challenge): boolean =>
  Boolean(challenge?.settings?.timelineType?.selfPaced);
export const getFlexibleSettings = (challenge: V1Challenge): SelfPaced =>
  challenge?.settings?.timelineType?.flexible;
export const getSelfPacedSettings = (challenge: V1Challenge): SelfPaced =>
  challenge?.settings?.timelineType?.selfPaced;

export const isDurationExpired = (
  startDate: string, // api-related format: '2020-10-06'
  duration: V1TimeDuration,
): boolean => {
  if (startDate && duration?.unit && !isNaN(duration?.value)) {
    const fixedStartDated = new Date(getRightDateFromBackend(startDate));
    const durationInDays = getDurationInDays(duration);

    if (isAfter(new Date(), addDays(fixedStartDated, durationInDays))) {
      return true;
    }
  }

  return false;
};

export const getDurationInDays = (duration: V1TimeDuration): number => {
  if (!duration?.unit || isNaN(duration?.value)) {
    return 0;
  }

  switch (duration.unit) {
    case V1DurationUnit.MINUTES:
      return Math.floor(duration.value / 24 / 60);
    case V1DurationUnit.HOURS:
      return Math.floor(duration.value / 24);
    case V1DurationUnit.DAYS:
      return duration.value;
    case V1DurationUnit.WEEKS:
      return duration.value * 7;
    default:
      return 0;
  }
};

export const isAvailableForJoinImmediately = (
  challenge: V1Challenge,
): boolean => !isPrivateChallenge(challenge);

export const getDurationForFlexyTimelines = (
  challenge: V1Challenge,
): V1TimeDuration => {
  return (
    getFlexibleSettings(challenge)?.duration ||
    getSelfPacedSettings(challenge)?.duration
  ); // duration is relevant only for flexible or self-paced here
};

export const isNeedToSelectStartDate = (
  challenge: V1Challenge,
  userType: IUserType,
  userPaidPlans: IUserPaidPlan[],
) => {
  const challengePaidPlans = getChallengePaidPlans(challenge.id, userPaidPlans);

  if (isFlexible(challenge)) {
    if (userType === ParticipantState.INVITED) {
      return true;
    }

    return !(
      (isPrivateChallenge(challenge) &&
        !isChallengeFree(
          challenge?.settings?.pricing,
          challengePaidPlans.length,
        ) &&
        !userTypeHandlers.isInPaymentState(userType)) ||
      userType === ParticipantState.JOIN_REQUESTED
    );
  }

  if (isSelfPaced(challenge)) {
    return !!challenge?.settings?.timelineType?.selfPaced?.duration?.value;
  }

  return false;
};

export const isPricingPickingForbidden = (
  challenge: V1Challenge,
  userType: IUserType,
  userPaidPlans: IUserPaidPlan[],
): boolean => {
  const challengePaidPlans = getChallengePaidPlans(challenge.id, userPaidPlans);

  return (
    isPrivateChallenge(challenge) &&
    !isChallengeFree(challenge?.settings?.pricing, challengePaidPlans.length) &&
    !userTypeHandlers.isInPaymentState(userType)
  );
};

export function isChallengeAlreadyStarted(
  challenge: V1Challenge,
  participantState?: ParticipantState,
): boolean {
  const startTime = isSpecific(challenge)
    ? challenge?.settings?.timelineType?.specific?.startTime
    : false;

  if (!startTime || userTypeHandlers.isJoinedAlready(participantState)) {
    return false;
  }

  return isAfter(
    endOfToday(),
    endOfDay(new Date(startTime.year, startTime.month - 1, startTime.day)),
  );
}
